import React, { useState } from 'react';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "What is agent memory?",
      answer: "Agent memory (also known as agentic memory) is an advanced AI memory system where autonomous agents intelligently manage, organize, and evolve memory structures. It enables AI applications to autonomously store, retrieve, and manage information with higher accuracy and faster retrieval than traditional memory systems."
    },
    {
      question: "How does MemU improve AI memory performance?",
      answer: "MemU improves AI memory performance through three key capabilities: higher accuracy via intelligent memory organization, faster retrieval through optimized indexing and caching, and lower cost by reducing redundant storage and API calls."
    },
    {
      question: "What are the benefits of agentic memory over traditional memory systems?",
      answer: "Agentic memory offers autonomous memory management, automatic organization and linking of related information, continuous evolution and optimization, contextual retrieval, and reduced human intervention compared to traditional static memory systems."
    },
    {
      question: "Is MemU open source?",
      answer: "Yes, MemU is an open-source agent memory framework. You can self-host it, contribute to the project, and integrate it into your LLM applications. We also offer a cloud version for easier deployment."
    },
    {
      question: "What LLM applications can use agent memory?",
      answer: "Agent memory can be used in various LLM applications including AI assistants, chatbots, conversational AI, AI companions, customer support bots, AI tutors, and any application that requires contextual memory and personalization."
    },
    {
      question: "How does agent memory differ from vector databases?",
      answer: "While vector databases provide semantic search capabilities, agent memory goes beyond by autonomously managing memory lifecycle, organizing information into interconnected knowledge graphs, and evolving memory structures over time based on usage patterns and relevance."
    },
    {
      question: "Can I integrate MemU with existing LLM frameworks?",
      answer: "Yes, MemU integrates seamlessly with popular LLM frameworks including LangChain, LangGraph, CrewAI, OpenAI, Anthropic, and more. Our SDK provides simple APIs for memory operations across different platforms."
    },
    {
      question: "What are the key features of MemU agent memory?",
      answer: "MemU offers autonomous memory organization, intelligent memory linking, continuous memory evolution, contextual retrieval, multi-modal memory support, real-time synchronization, and extensive integration options with LLM frameworks."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="faq-section" style={styles.section}>
      <div className="container" style={styles.container}>
        <div className="section-header" style={styles.header}>
          <h2 style={styles.title}>Frequently Asked Questions About Agent Memory</h2>
          <p style={styles.subtitle}>Everything you need to know about agent memory and MemU platform</p>
        </div>
        
        <div className="faq-list" style={styles.faqList}>
          {faqs.map((faq, index) => (
            <div 
              key={index} 
              className="faq-item" 
              style={{
                ...styles.faqItem,
                borderColor: openIndex === index ? '#7DCBF7' : '#e2e8f0'
              }}
              itemScope 
              itemProp="mainEntity" 
              itemType="https://schema.org/Question"
            >
              <button
                className="faq-question"
                style={styles.question}
                onClick={() => toggleFAQ(index)}
                aria-expanded={openIndex === index}
              >
                <h3 itemProp="name" style={styles.questionText}>
                  {faq.question}
                </h3>
                <span style={{
                  ...styles.icon,
                  transform: openIndex === index ? 'rotate(180deg)' : 'rotate(0deg)'
                }}>
                  ▼
                </span>
              </button>
              
              {openIndex === index && (
                <div 
                  className="faq-answer" 
                  style={styles.answer}
                  itemScope 
                  itemProp="acceptedAnswer" 
                  itemType="https://schema.org/Answer"
                >
                  <p itemProp="text" style={styles.answerText}>{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
        
        <div style={styles.ctaBox}>
          <h3 style={styles.ctaTitle}>Still have questions about agent memory?</h3>
          <p style={styles.ctaText}>Check out our documentation or reach out to our team</p>
          <div style={styles.ctaButtons}>
            <a href="/docs" style={styles.ctaButton}>
              Read Documentation
            </a>
            <a href="/pricing" style={{...styles.ctaButton, ...styles.ctaButtonSecondary}}>
              View Pricing
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: {
    padding: '80px 20px',
    backgroundColor: '#f8fafc',
  },
  container: {
    maxWidth: '900px',
    margin: '0 auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '50px',
  },
  title: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '16px',
  },
  subtitle: {
    fontSize: '18px',
    color: '#64748b',
    lineHeight: '1.6',
  },
  faqList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  faqItem: {
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '2px solid',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
  },
  question: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
  },
  questionText: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    margin: 0,
    flex: 1,
  },
  icon: {
    fontSize: '14px',
    color: '#7DCBF7',
    transition: 'transform 0.3s ease',
    marginLeft: '16px',
  },
  answer: {
    padding: '0 24px 20px 24px',
    animation: 'fadeIn 0.3s ease',
  },
  answerText: {
    fontSize: '16px',
    color: '#475569',
    lineHeight: '1.7',
    margin: 0,
  },
  ctaBox: {
    marginTop: '60px',
    padding: '40px',
    backgroundColor: 'white',
    borderRadius: '16px',
    border: '2px solid #e2e8f0',
    textAlign: 'center',
  },
  ctaTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '12px',
  },
  ctaText: {
    fontSize: '16px',
    color: '#64748b',
    marginBottom: '24px',
  },
  ctaButtons: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  ctaButton: {
    display: 'inline-block',
    padding: '12px 32px',
    backgroundColor: '#7DCBF7',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '16px',
    transition: 'all 0.3s ease',
    border: '2px solid #7DCBF7',
  },
  ctaButtonSecondary: {
    backgroundColor: 'transparent',
    color: '#7DCBF7',
  },
};

export default FAQ;
